<template>
  <a-modal title="Chat umbenennen" v-model:open="renameModalVisible" okText="Speichern" @ok="renameCase()">
    <a-input v-model:value="caseNewName" placeholder="Basic usage" />
  </a-modal>
  <div style="height: 100%; overflow-y: auto" class="sidebar_dynamic_item llm_doc_browser">
    <div class="llm_sidebar_toolbar">
      <a-input
        v-model:value="searchQuery"
        placeholder="Chattitel suchen…"
        allow-clear
        size="small"
        class="llm_sidebar_search"
        @press-enter="onSearchSubmit"
      >
        <template #prefix>
          <SearchOutlined style="color: rgba(0, 0, 0, 0.45)" />
        </template>
      </a-input>
      <div class="llm_sidebar_chips">
        <a-range-picker
          v-model:value="rangePickerValue"
          :presets="datePresets"
          :allow-empty="[true, true]"
          format="DD.MM.YYYY"
          :placeholder="['Von', 'Bis']"
          size="small"
          class="llm_sidebar_range_picker"
          @change="onRangePickerChange"
        >
          <template #suffixIcon><CalendarOutlined /></template>
        </a-range-picker>
      </div>
    </div>

    <a-menu
      style="background-color: #f0f2f5"
      @select="onMenuItemSelected"
      v-model:openKeys="openKeys"
      v-model:selectedKeys="selectedKeys"
      :inline-indent="8"
      mode="inline"
    >
      <a-button
        type="primary"
        style="width: 100%; margin-top: 12px"
        @click="
          () => {
            selectedKeys = [];
            eventBus.emit('on-llm-home');
          }
        "
      >
        <template #icon>
          <HomeOutlined />
        </template>
        Startseite | Neuer Chat
      </a-button>

      <template v-if="hasResults">
        <a-sub-menu
          @titleClick="
            ({ key }: { key: string }) => {
              if (collapsed) {
                nextTick(() => {
                  openKeys.push(key);
                });
              }
            }
          "
          class="llm_history_item"
          :key="groupKey"
          v-for="(group, groupKey) of cases"
        >
          <template #title>{{ groupKey }}</template>
          <a-menu-item v-for="item of group" :key="item.id">
            <div class="llm_case_row">
              <span class="llm_case_title">{{ item.name }}</span>
              <a-tooltip :title="formatAbsoluteDate(item.creation_date)" placement="top">
                <span class="llm_case_date">{{ formatRelativeDate(item.creation_date) }}</span>
              </a-tooltip>
              <span class="llm_case_actions" @click.stop>
                <a-dropdown :trigger="['click']" placement="bottomRight">
                  <a-button type="text" size="small" class="llm_case_action_btn" @click.stop>
                    <MoreOutlined />
                  </a-button>
                  <template #overlay>
                    <a-menu @click="(e: any) => e.domEvent && e.domEvent.stopPropagation()">
                      <a-menu-item key="rename" @click="openRenameDialog(item.id, item.name)">
                        <EditOutlined /> Umbenennen
                      </a-menu-item>
                      <a-menu-item key="export" @click="eventBus.emit('on-case-export', { id: item.id })">
                        <DownloadOutlined /> Exportieren
                      </a-menu-item>
                      <a-menu-divider />
                      <a-popconfirm
                        title="Diesen Chat löschen?"
                        ok-text="Löschen"
                        cancel-text="Abbrechen"
                        ok-type="danger"
                        placement="left"
                        @confirm="eventBus.emit('on-case-delete', { id: item.id })"
                      >
                        <a-menu-item key="delete" danger @click.stop>
                          <DeleteOutlined /> Löschen
                        </a-menu-item>
                      </a-popconfirm>
                    </a-menu>
                  </template>
                </a-dropdown>
              </span>
            </div>
          </a-menu-item>
        </a-sub-menu>
      </template>

      <a-empty
        v-else-if="!loading"
        :description="hasActiveFilter ? 'Keine passenden Chats' : 'Noch keine Chats'"
        style="margin-top: 24px"
      />

      <a-button
        v-if="hasResults && pagination.current < pagination.total"
        style="width: 100%; margin-top: 8px"
        @click="
          () => {
            collapsed = false;
            loadMoreCases();
          }
        "
      >
        Mehr laden
      </a-button>
    </a-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, nextTick, onMounted, onUnmounted, reactive, ref, toRefs, watch, VNode } from "vue";
import {
  HomeOutlined,
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined,
  CalendarOutlined,
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { debounce } from "lodash-es";
import dayjs, { Dayjs } from "dayjs";

import { updateCase } from "../../api/cases";
import eventBus from "../workflow/eventBus";
import { useLLMCaseListStore, LLMCaseListFilter } from "../../store/modules/llmCaseList";
import { formatAbsoluteDate, formatRelativeDate } from "../../utils/timeUtils";
interface State {
  collapsed: boolean;
  selectedKeys: string[];
  openKeys: string[];
  preOpenKeys: string[];
  width: number;
  renameModalVisible: boolean;
  renamingCaseId: string;
  caseNewName: string;
  loading: boolean;
}


export default defineComponent({
  components: {
    HomeOutlined,
    EditOutlined,
    DownloadOutlined,
    DeleteOutlined,
    CalendarOutlined,
    MoreOutlined,
    SearchOutlined,
  },

  setup() {
    const store = useLLMCaseListStore();
    const { cases, pagination } = storeToRefs(store);
    const { loadMoreCases } = store;

    const state = reactive<State>({
      collapsed: true,
      selectedKeys: [],
      openKeys: [""],
      preOpenKeys: [""],
      width: 400,
      renameModalVisible: false,
      renamingCaseId: "",
      caseNewName: "",
      loading: false,
    });

    const searchQuery = ref("");
    const rangePickerValue = ref<[Dayjs, Dayjs] | null>([dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")]);

    const datePresets = [
      { label: "Heute", value: [dayjs().startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
      { label: "Letzte Woche", value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
      { label: "Letzter Monat", value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
      { label: "Aktuelles Jahr", value: [dayjs().startOf("year"), dayjs().endOf("year")] as [Dayjs, Dayjs] },
    ];

    const hasResults = computed(() => Object.keys(cases.value).length > 0);
    const hasActiveFilter = computed(
      () => searchQuery.value.trim().length > 0 || rangePickerValue.value !== null
    );

    const buildFilter = (): LLMCaseListFilter => {
      const filter: LLMCaseListFilter = {};
      const trimmed = searchQuery.value.trim();
      if (trimmed) filter.name = trimmed;
      if (rangePickerValue.value) {
        const [start, end] = rangePickerValue.value;
        filter.fromDate = start.startOf("day").toDate().toISOString();
        filter.toDate = end.endOf("day").toDate().toISOString();
      }
      return filter;
    };

    const applyFilters = async () => {
      state.loading = true;
      try {
        await store.updateCaseList(buildFilter());
      } finally {
        state.loading = false;
      }
    };

    const debouncedApply = debounce(applyFilters, 300);

    watch(searchQuery, () => {
      debouncedApply();
    });

    const onSearchSubmit = () => {
      debouncedApply.cancel();
      applyFilters();
    };
    const onRangePickerChange = () => {
      applyFilters();
    };

    const router = useRouter();
    const removeAfterEach = router.afterEach((to) => {
      if (to.name === "Large Language Model") {
        applyFilters();
      }
    });

    onMounted(() => {
      applyFilters();
    });

    onUnmounted(() => {
      removeAfterEach();
      debouncedApply.cancel();
    });

    const route = useRoute();
    watch(
      () => route.params,
      () => {
        if (route.params["caseId"] && (route.params["caseId"] as string).length > 8) {
          state.selectedKeys = [route.params["caseId"] as string];
        } else {
          state.selectedKeys = [];
        }
      },
      { deep: true }
    );

    watch(
      () => state.openKeys,
      (_val, oldVal) => {
        state.preOpenKeys = oldVal;
      }
    );

    const containerRef = ref<VNode | null>(null);

    const onMenuItemSelected = ({ key }: { key: string }) => {
      eventBus.emit("on-case-selected", { id: key, interuptProgress: true });
    };

    const openRenameDialog = (caseId: string, curName: string) => {
      state.renameModalVisible = true;
      state.renamingCaseId = caseId;
      state.caseNewName = curName;
    };

    const renameCase = async () => {
      try {
        await updateCase({ id: state.renamingCaseId, title: state.caseNewName, type: "llm" });
        state.renamingCaseId = "";
        state.renameModalVisible = false;
        await applyFilters();
        return Promise.resolve();
      } catch {
        return Promise.reject();
      }
    };

    return {
      cases,
      pagination,
      loadMoreCases,
      ...toRefs(state),
      onMenuItemSelected,
      openRenameDialog,
      renameCase,
      containerRef,
      eventBus,
      nextTick,
      searchQuery,
      rangePickerValue,
      datePresets,
      onRangePickerChange,
      onSearchSubmit,
      hasResults,
      hasActiveFilter,
      formatAbsoluteDate,
      formatRelativeDate,
      dayjs,
    };
  },
});
</script>

<style>
.llm_doc_browser .ant-layout-sider {
  margin-right: 12px;
}
.llm_doc_browser {
  background-color: #f5f7fa !important;
}
.llm_doc_browser .ant-menu {
  background-color: #f5f7fa !important;
}
.llm_doc_browser .ant-menu-submenu-title {
  padding-left: 8px !important;
  padding-right: 8px !important;
}
.llm_doc_browser .ant-menu-item {
  padding-left: 12px !important;
  padding-right: 8px !important;
}
.llm_doc_browser .ant-menu-submenu .ant-menu-item {
  padding-left: 12px !important;
}
</style>

<style scoped>
.llm_sidebar_toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f5f7fa;
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}


.llm_sidebar_chips {
  display: flex;
  align-items: center;
  gap: 6px;
}


/* Align search input and range picker shape (global borderRadius:12 makes them inconsistent). */
.llm_sidebar_search :deep(.ant-input-affix-wrapper) {
  border-radius: 6px !important;
}
.llm_sidebar_range_picker {
  width: 100%;
  border-radius: 6px !important;
}
.llm_sidebar_range_picker :deep(.ant-picker) {
  border-radius: 6px !important;
}

.llm_sidebar_custom_btn {
  flex: 0 0 auto;
}

.llm_sidebar_custom_label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  padding: 0 4px;
  text-align: center;
}

.llm_case_row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.llm_case_title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.llm_case_date {
  flex: 0 0 auto;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  font-variant-numeric: tabular-nums;
}

.llm_case_actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  opacity: 0.55;
  transition: opacity 0.15s ease;
}

:deep(.ant-menu-item):hover .llm_case_actions,
:deep(.ant-menu-item-selected) .llm_case_actions {
  opacity: 1;
}

.llm_case_action_btn {
  padding: 0 4px !important;
  height: 28px !important;
  width: 28px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.llm_case_action_btn :deep(.anticon) {
  font-size: 18px;
}
</style>

<style>
.llm_history_item {
  padding-left: 0px !important;
  padding-right: 0px !important;
}
.ant-popover {
  z-index: 5000 !important;
}
</style>
